import * as React from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { Plus, ArrowRight, Megaphone, ChartLine } from 'lucide-react';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Field } from '@/components/ui/field';
import { Select } from '@/components/ui/select';
import {
  useDripCampaigns,
  useCreateCampaign,
  type CampaignKind,
  type Audience,
  type CampaignStatus,
  type DripCampaign,
} from '@/lib/drip';
import { cn } from '@/lib/cn';

export const Route = createFileRoute('/_app/sms/campaigns')({
  staticData: { crumb: 'Kampanie' },
  component: CampaignsPage,
});

const KIND_LABEL: Record<CampaignKind, string> = {
  booking: 'Wokół wizyty',
  recall: 'Recall (powrót po przerwie)',
  newsletter: 'Newsletter (broadcast)',
};

const AUDIENCE_LABEL: Record<Audience, string> = {
  booking_confirmed: 'Potwierdzone wizyty',
  booking_completed: 'Po wizycie',
  manual: 'Ręcznie wybrana lista',
};

const STATUS_LABEL: Record<CampaignStatus, string> = {
  draft: 'Szkic',
  active: 'Aktywna',
  paused: 'Wstrzymana',
  archived: 'Zarchiwizowana',
};

function CampaignsPage() {
  const campaigns = useDripCampaigns();
  const create = useCreateCampaign();
  const [showNew, setShowNew] = React.useState(false);
  const [newName, setNewName] = React.useState('');
  const [newKind, setNewKind] = React.useState<CampaignKind>('booking');
  const [newAudience, setNewAudience] = React.useState<Audience>('booking_confirmed');
  const [err, setErr] = React.useState<string | null>(null);

  const onCreate = async () => {
    setErr(null);
    try {
      await create.mutateAsync({ name: newName, kind: newKind, audience: newAudience });
      setNewName('');
      setShowNew(false);
    } catch (e) {
      setErr((e as Error).message);
    }
  };

  return (
    <div className="space-y-6">
      <header>
        <h1 className="font-display text-2xl font-semibold tracking-tight text-text">
          Kampanie SMS
        </h1>
        <p className="mt-1 text-sm text-text-muted">
          Sekwencje SMS-ów wokół wizyt, recall i broadcast. Każda kampania ma wiele kroków
          (np. potwierdzenie + przypomnienie + podziękowanie + recall za 6 miesięcy).
        </p>
      </header>

      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Megaphone className="size-5" aria-hidden="true" /> Wszystkie kampanie
            </CardTitle>
            <CardDescription>
              Klinika tworzy własne kampanie. Przykład: „Onboarding stomatologii" =
              potwierdzenie ⟶ T-24h przypomnienie ⟶ T+1h podziękowanie ⟶ T+6 mies. recall.
            </CardDescription>
          </div>
          <Button type="button" onClick={() => setShowNew((v) => !v)}>
            <Plus className="size-4" aria-hidden="true" /> Nowa kampania
          </Button>
        </CardHeader>
        <CardContent>
          {showNew ? (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (newName) onCreate();
              }}
              className="mb-5 grid gap-3 rounded-md bg-surface p-4 ring-1 ring-black/5 md:grid-cols-3"
            >
              <Field label="Nazwa" htmlFor="new-name" required>
                <Input
                  id="new-name"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="Onboarding stomatologii"
                />
              </Field>
              <Field label="Typ" htmlFor="new-kind">
                <Select
                  id="new-kind"
                  value={newKind}
                  onChange={(e) => setNewKind(e.target.value as CampaignKind)}
                >
                  {(Object.keys(KIND_LABEL) as CampaignKind[]).map((k) => (
                    <option key={k} value={k}>
                      {KIND_LABEL[k]}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Odbiorcy" htmlFor="new-aud">
                <Select
                  id="new-aud"
                  value={newAudience}
                  onChange={(e) => setNewAudience(e.target.value as Audience)}
                >
                  {(Object.keys(AUDIENCE_LABEL) as Audience[]).map((a) => (
                    <option key={a} value={a}>
                      {AUDIENCE_LABEL[a]}
                    </option>
                  ))}
                </Select>
              </Field>
              <div className="md:col-span-3 flex items-center justify-end gap-2">
                {err ? <p className="text-xs text-error">{err}</p> : null}
                <Button type="button" variant="ghost" onClick={() => setShowNew(false)}>
                  Anuluj
                </Button>
                <Button type="submit" disabled={!newName || create.isPending}>
                  Utwórz
                </Button>
              </div>
            </form>
          ) : null}

          {campaigns.isPending ? (
            <p className="text-sm text-text-muted">Ładowanie…</p>
          ) : (campaigns.data ?? []).length === 0 ? (
            <p className="rounded-md bg-surface p-4 text-sm text-text-muted ring-1 ring-black/5">
              Brak kampanii. Stwórz pierwszą — dobrym startem jest „Wokół wizyty" z odbiorcami
              „Potwierdzone wizyty".
            </p>
          ) : (
            <ul role="list" className="space-y-3">
              {(campaigns.data ?? []).map((c) => (
                <CampaignRow key={c.id} campaign={c} />
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function CampaignRow({ campaign }: { campaign: DripCampaign }) {
  return (
    <li>
      <Link
        to={'/sms/campaigns/$id' as never}
        params={{ id: campaign.id } as never}
        className="group flex items-start justify-between gap-3 rounded-[var(--radius-card)] bg-bg p-4 ring-1 ring-black/5 transition hover:ring-black/10"
      >
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-2">
            <p className="font-display text-base font-semibold text-text">{campaign.name}</p>
            <StatusBadge status={campaign.status} />
          </div>
          <p className="mt-1 text-xs text-text-muted">
            <ChartLine className="mr-1 inline size-3" aria-hidden="true" />
            {KIND_LABEL[campaign.kind]} · {AUDIENCE_LABEL[campaign.audience]} ·{' '}
            <span className="tabular-nums">{campaign.step_count ?? 0}</span>{' '}
            {campaign.step_count === 1 ? 'krok' : 'kroków'}
          </p>
        </div>
        <ArrowRight
          className="size-4 text-text-subtle transition group-hover:translate-x-0.5 group-hover:text-text"
          aria-hidden="true"
        />
      </Link>
    </li>
  );
}

function StatusBadge({ status }: { status: CampaignStatus }) {
  const styles: Record<CampaignStatus, string> = {
    draft: 'bg-surface text-text-muted ring-border',
    active: 'bg-success/10 text-success ring-success/20',
    paused: 'bg-warning/10 text-warning ring-warning/20',
    archived: 'bg-surface text-text-subtle ring-border',
  };
  return (
    <span className={cn('rounded-full px-2 py-0.5 text-xs ring-1', styles[status])}>
      {STATUS_LABEL[status]}
    </span>
  );
}
